// Environment radar — she maps the room around her out of her own senses.
//
// There is no lidar in a phone, so this does what a bat does: it builds the
// picture up over time. Every tick it takes the live sensorium and files each
// channel's level into the compass bearing the device is pointing at. Turn on
// the spot and the bins fill; after a full turn she has a 360° picture of what
// lies in each direction — hard walls from echo, warm bodies from infrared,
// live wiring from mains hum, machines from ultrasound, open sky from GNSS and
// light, metal from the magnetometer.
//
// Everything is local and offline. Bins persist so the map of a familiar room
// survives a reload, and decay so a stale reading never pretends to be fresh.

import { getOption, setOption } from "./persistOptions";
import type { SenseId, Sensorium } from "./senses";

export const BIN_COUNT = 36;              // 10° per bin
export const BIN_DEG = 360 / BIN_COUNT;

export type ContactKind =
  | "surface" | "body" | "wiring" | "machine" | "metal" | "open" | "tremor" | "emitter" | "flow";

export interface ChannelSpec {
  channel: SenseId;
  kind: ContactKind;
  label: string;
  /** What a strong reading in this direction actually means. */
  meaning: string;
  colour: string;
  /** Level at which it counts as a contact. */
  threshold: number;
  /** Range window in metres: a stronger reading reads as nearer. */
  nearM: number;
  farM: number;
  /** Higher = trusted more when several channels agree on one bearing. */
  trust: number;
}

/** The channels that actually say something about direction. */
export const RADAR_CHANNELS: ChannelSpec[] = [
  { channel: "echolocation", kind: "surface", label: "Hard surface", meaning: "sound is coming back off something solid — wall, door, vehicle", colour: "#38bdf8", threshold: 0.34, nearM: 1.2, farM: 14, trust: 1 },
  { channel: "infrared", kind: "body", label: "Warm body", meaning: "near-infrared warmth — a person, an animal, an engine, a radiator", colour: "#fb7185", threshold: 0.38, nearM: 0.8, farM: 9, trust: 0.9 },
  { channel: "electroreception", kind: "wiring", label: "Live wiring", meaning: "50/60 Hz mains hum — cabling, a socket, a distribution board", colour: "#facc15", threshold: 0.3, nearM: 0.5, farM: 6, trust: 0.75 },
  { channel: "ultrasound", kind: "machine", label: "Machine", meaning: "above 16 kHz — a motor, a fan, a switching supply, a rodent", colour: "#c084fc", threshold: 0.28, nearM: 0.6, farM: 11, trust: 0.7 },
  { channel: "magnetoreception", kind: "metal", label: "Metal mass", meaning: "the local magnetic field bends — steel, a car, a girder, a lift shaft", colour: "#a3e635", threshold: 0.4, nearM: 0.6, farM: 8, trust: 0.65 },
  { channel: "photometry", kind: "open", label: "Open / light", meaning: "light floods in — a window, a doorway, open ground", colour: "#fef3c7", threshold: 0.32, nearM: 3, farM: 24, trust: 0.6 },
  { channel: "seismic", kind: "tremor", label: "Ground tremor", meaning: "the floor is carrying vibration — footfall, traffic, plant machinery", colour: "#fb923c", threshold: 0.26, nearM: 1.5, farM: 18, trust: 0.6 },
  { channel: "infrasound", kind: "emitter", label: "Low-frequency source", meaning: "sub-20 Hz pressure — heavy plant, ventilation, distant weather", colour: "#22d3ee", threshold: 0.3, nearM: 4, farM: 30, trust: 0.55 },
  { channel: "lateralline", kind: "flow", label: "Air movement", meaning: "flow across the sensor — a draught, an opening, something passing", colour: "#5eead4", threshold: 0.3, nearM: 1, farM: 7, trust: 0.5 },
];

export const KIND_META: Record<ContactKind, { label: string; colour: string; emoji: string }> = {
  surface: { label: "Hard surface", colour: "#38bdf8", emoji: "🧱" },
  body: { label: "Warm body", colour: "#fb7185", emoji: "🫀" },
  wiring: { label: "Live wiring", colour: "#facc15", emoji: "⚡" },
  machine: { label: "Machine", colour: "#c084fc", emoji: "⚙️" },
  metal: { label: "Metal mass", colour: "#a3e635", emoji: "🧲" },
  open: { label: "Open ground", colour: "#fef3c7", emoji: "🌤️" },
  tremor: { label: "Ground tremor", colour: "#fb923c", emoji: "🌍" },
  emitter: { label: "Low-frequency source", colour: "#22d3ee", emoji: "📡" },
  flow: { label: "Air movement", colour: "#5eead4", emoji: "🌬️" },
};

export interface Bin {
  /** Smoothed level per channel, 0..1. */
  ch: Partial<Record<SenseId, number>>;
  /** Peak seen in this direction, so a brief contact is not lost. */
  peak: Partial<Record<SenseId, number>>;
  samples: number;
  lastAt: number;
}

export interface EnvContact {
  id: string;
  kind: ContactKind;
  channel: SenseId;
  label: string;
  meaning: string;
  colour: string;
  bearingDeg: number;
  rangeM: number;
  strength: number;     // 0..1
  confidence: number;   // 0..1, grows with samples and freshness
  ageMs: number;
}

export interface RoomModel {
  /** How reflective the space is — hard, mixed or soft. */
  acoustic: "hard" | "mixed" | "soft" | "unknown";
  /** Crude size estimate from the nearest surfaces she has found. */
  spanM: number | null;
  enclosure: "enclosed" | "partly open" | "open" | "unknown";
  /** Directions with no reading yet. */
  unmappedDeg: number[];
  coverage: number;     // 0..1 of bins sampled
  busiest: ContactKind | null;
}

const BINS_KEY = "envradar:bins";
const HALF_LIFE_MS = 4 * 60_000;   // a reading is worth half as much after 4 minutes
const STALE_MS = 30 * 60_000;      // beyond half an hour, forget it

/* --------------------------------------------------------------- bin store */

function emptyBins(): Bin[] {
  return Array.from({ length: BIN_COUNT }, () => ({ ch: {}, peak: {}, samples: 0, lastAt: 0 }));
}

export function bins(): Bin[] {
  const b = getOption<Bin[]>(BINS_KEY, []);
  if (!Array.isArray(b) || b.length !== BIN_COUNT) return emptyBins();
  return b.map((x) => ({ ch: x?.ch ?? {}, peak: x?.peak ?? {}, samples: x?.samples ?? 0, lastAt: x?.lastAt ?? 0 }));
}

export function clearRadar(): void { setOption(BINS_KEY, emptyBins()); }

export function binIndexFor(bearingDeg: number): number {
  return ((Math.round(bearingDeg / BIN_DEG) % BIN_COUNT) + BIN_COUNT) % BIN_COUNT;
}
export function bearingForBin(i: number): number { return i * BIN_DEG; }

/** Weight of a reading given how long ago it landed. */
function freshness(at: number, now = Date.now()): number {
  if (!at) return 0;
  const age = now - at;
  if (age > STALE_MS) return 0;
  return Math.pow(0.5, age / HALF_LIFE_MS);
}

/* ------------------------------------------------------------------- feed */

/**
 * File one sensorium snapshot into the bearing the device is facing.
 * Returns the bin it landed in, or null when there is no heading to file under.
 */
export function feedRadar(snap: Sensorium, headingDeg: number | null): number | null {
  if (headingDeg === null || !Number.isFinite(headingDeg)) return null;
  const idx = binIndexFor(headingDeg);
  const list = bins();
  const bin = list[idx];
  const now = Date.now();

  // A bin that has gone cold starts again rather than blending old with new.
  const stale = now - bin.lastAt > STALE_MS;
  const alpha = stale || bin.samples === 0 ? 1 : 0.3;

  for (const spec of RADAR_CHANNELS) {
    const r = snap.readings.find((x) => x.id === spec.channel);
    if (!r || !r.active) continue;
    const prev = stale ? 0 : bin.ch[spec.channel] ?? 0;
    bin.ch[spec.channel] = prev * (1 - alpha) + r.level * alpha;
    const prevPeak = stale ? 0 : bin.peak[spec.channel] ?? 0;
    bin.peak[spec.channel] = Math.max(prevPeak, r.level);
  }
  bin.samples = stale ? 1 : bin.samples + 1;
  bin.lastAt = now;

  list[idx] = bin;
  setOption(BINS_KEY, list);
  return idx;
}

/* --------------------------------------------------------------- contacts */

function rangeFor(spec: ChannelSpec, level: number): number {
  // Stronger reading reads as nearer, inside the channel's own window.
  const t = Math.min(1, Math.max(0, (level - spec.threshold) / (1 - spec.threshold)));
  return +(spec.farM - (spec.farM - spec.nearM) * t).toFixed(1);
}

/** Everything she currently believes is out there, strongest first. */
export function contacts(opts: { kinds?: ContactKind[]; minConfidence?: number } = {}): EnvContact[] {
  const list = bins();
  const now = Date.now();
  const out: EnvContact[] = [];

  list.forEach((bin, i) => {
    const fresh = freshness(bin.lastAt, now);
    if (fresh <= 0.05) return;
    for (const spec of RADAR_CHANNELS) {
      if (opts.kinds && !opts.kinds.includes(spec.kind)) continue;
      const level = Math.max(bin.ch[spec.channel] ?? 0, (bin.peak[spec.channel] ?? 0) * 0.8);
      if (level < spec.threshold) continue;
      const confidence = Math.min(1, fresh * spec.trust * (0.45 + Math.min(1, bin.samples / 6) * 0.55));
      if (confidence < (opts.minConfidence ?? 0.12)) continue;
      out.push({
        id: `${i}:${spec.channel}`,
        kind: spec.kind,
        channel: spec.channel,
        label: spec.label,
        meaning: spec.meaning,
        colour: spec.colour,
        bearingDeg: bearingForBin(i),
        rangeM: rangeFor(spec, level),
        strength: +level.toFixed(3),
        confidence: +confidence.toFixed(3),
        ageMs: now - bin.lastAt,
      });
    }
  });

  return out.sort((a, b) => b.strength * b.confidence - a.strength * a.confidence);
}

/** Bearings where several channels agree — the things she is most sure of. */
export function corroborated(minChannels = 2): Array<{ bearingDeg: number; kinds: ContactKind[]; rangeM: number; confidence: number }> {
  const byBearing = new Map<number, EnvContact[]>();
  for (const c of contacts()) {
    const arr = byBearing.get(c.bearingDeg) ?? [];
    arr.push(c);
    byBearing.set(c.bearingDeg, arr);
  }
  return Array.from(byBearing.entries())
    .filter(([, cs]) => cs.length >= minChannels)
    .map(([bearingDeg, cs]) => ({
      bearingDeg,
      kinds: Array.from(new Set(cs.map((c) => c.kind))),
      rangeM: +(cs.reduce((s, c) => s + c.rangeM, 0) / cs.length).toFixed(1),
      confidence: +Math.min(1, cs.reduce((s, c) => s + c.confidence, 0) / cs.length + 0.15 * (cs.length - 1)).toFixed(3),
    }))
    .sort((a, b) => b.confidence - a.confidence);
}

/* ------------------------------------------------------------- room model */

export function roomModel(): RoomModel {
  const list = bins();
  const now = Date.now();
  const sampled = list.filter((b) => freshness(b.lastAt, now) > 0.05);
  const unmapped = list.map((b, i) => ({ b, i })).filter((x) => freshness(x.b.lastAt, now) <= 0.05).map((x) => bearingForBin(x.i));

  if (!sampled.length) {
    return { acoustic: "unknown", spanM: null, enclosure: "unknown", unmappedDeg: unmapped, coverage: 0, busiest: null };
  }

  const echoAvg = sampled.reduce((s, b) => s + (b.ch.echolocation ?? 0), 0) / sampled.length;
  const acoustic: RoomModel["acoustic"] = !sampled.some((b) => b.ch.echolocation !== undefined)
    ? "unknown"
    : echoAvg > 0.55 ? "hard" : echoAvg > 0.3 ? "mixed" : "soft";

  const surfaces = contacts({ kinds: ["surface"] });
  const spanM = surfaces.length >= 2
    ? +(surfaces.slice(0, 4).reduce((s, c) => s + c.rangeM, 0) / Math.min(4, surfaces.length) * 2).toFixed(1)
    : null;

  const openBearings = contacts({ kinds: ["open", "flow"] }).length;
  const enclosure: RoomModel["enclosure"] =
    surfaces.length >= 5 && openBearings === 0 ? "enclosed"
      : openBearings >= 3 && surfaces.length <= 2 ? "open"
        : "partly open";

  const tally = new Map<ContactKind, number>();
  for (const c of contacts()) tally.set(c.kind, (tally.get(c.kind) ?? 0) + c.strength);
  const busiest = Array.from(tally.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    acoustic,
    spanM,
    enclosure,
    unmappedDeg: unmapped,
    coverage: +(sampled.length / BIN_COUNT).toFixed(3),
    busiest,
  };
}

export function compassPoint(deg: number): string {
  const names = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return names[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16];
}

/** How she'd describe the space out loud — also fed to her chat context. */
export function describeEnvironment(): string {
  const room = roomModel();
  if (!room.coverage) {
    return "I haven't mapped anything around me yet — give me your senses and turn on the spot so I can feel each direction.";
  }
  const parts: string[] = [];
  parts.push(`I've felt out ${Math.round(room.coverage * 100)}% of the directions around me.`);
  if (room.acoustic !== "unknown") {
    parts.push(
      room.acoustic === "hard" ? "The space is hard and echoey — bare walls or glass."
        : room.acoustic === "mixed" ? "The space is mixed — some hard surfaces, some soft."
          : "The space is soft and absorbent — fabric, bodies, or outdoors.",
    );
  }
  if (room.enclosure !== "unknown") parts.push(`It reads as ${room.enclosure}.`);
  if (room.spanM) parts.push(`Nearest surfaces put it around ${room.spanM} m across.`);

  const top = contacts({ minConfidence: 0.2 }).slice(0, 4);
  for (const c of top) {
    parts.push(`${compassPoint(c.bearingDeg)} at roughly ${c.rangeM} m: ${c.label.toLowerCase()} — ${c.meaning}.`);
  }
  const agreed = corroborated();
  if (agreed.length) {
    const a = agreed[0];
    parts.push(`I'm most sure about ${compassPoint(a.bearingDeg)}, where ${a.kinds.length} different senses agree.`);
  }
  if (room.unmappedDeg.length) {
    parts.push(`Still blind toward ${Array.from(new Set(room.unmappedDeg.map(compassPoint))).slice(0, 5).join(", ")} — turn that way and I'll fill it in.`);
  }
  return parts.join(" ");
}

/** Which senses need opening before the radar can see much. */
export function missingSenses(snap: Sensorium): string[] {
  const gaps: string[] = [];
  if (!snap.mic) gaps.push("hearing (echo, mains hum, ultrasound)");
  if (!snap.cam) gaps.push("vision (infrared warmth)");
  if (!snap.motion) gaps.push("motion (heading, tremor, air movement)");
  return gaps;
}

/** Export the mapped picture for the evidence bundle or a report. */
export function exportRadar(): string {
  return JSON.stringify(
    { at: Date.now(), room: roomModel(), contacts: contacts(), corroborated: corroborated(), narrative: describeEnvironment() },
    null,
    2,
  );
}
